import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  type User as FirebaseUser,
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { setAuthToken, setTokenRefreshCallback, get } from '@/lib/api-client';
import { db, dbReady } from '@/lib/db';
import { settingsService } from '@/services/settings.service';
import type { User, AuthState, LoginCredentials } from '@/types/auth.types';

interface UserProfileResponse {
  uid: string;
  email: string | null;
  organizationId: string | null;
  role: string;
}

// Validate required Firebase environment variables
const VITE_FIREBASE_API_KEY = import.meta.env.VITE_FIREBASE_API_KEY;
const VITE_FIREBASE_AUTH_DOMAIN = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
const VITE_FIREBASE_PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID;

if (!VITE_FIREBASE_API_KEY) {
  throw new Error('Missing required environment variable: VITE_FIREBASE_API_KEY');
}
if (!VITE_FIREBASE_AUTH_DOMAIN) {
  throw new Error('Missing required environment variable: VITE_FIREBASE_AUTH_DOMAIN');
}
if (!VITE_FIREBASE_PROJECT_ID) {
  throw new Error('Missing required environment variable: VITE_FIREBASE_PROJECT_ID');
}

// Firebase configuration - loaded from environment variables
const firebaseConfig = {
  apiKey: VITE_FIREBASE_API_KEY,
  authDomain: VITE_FIREBASE_AUTH_DOMAIN,
  projectId: VITE_FIREBASE_PROJECT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

interface AuthContextValue extends AuthState {
  signIn: (credentials: LoginCredentials) => Promise<void>;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Transform Firebase user to app User type
 */
function transformUser(
  firebaseUser: FirebaseUser,
  profile?: UserProfileResponse
): User {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    role: profile?.role || 'viewer',
    organizationId: profile?.organizationId || null,
    createdAt: firebaseUser.metadata.creationTime || new Date().toISOString(),
    lastLoginAt: firebaseUser.metadata.lastSignInTime,
  };
}

/**
 * Fetch user profile from backend
 */
async function fetchUserProfile(): Promise<UserProfileResponse | null> {
  try {
    return await get<UserProfileResponse>('/auth/me');
  } catch (error) {
    console.error('Failed to fetch user profile:', error);
    return null;
  }
}

const CACHED_PROFILE_KEY = 'cachedUserProfile';

/**
 * Cache user profile in Dexie for offline reopen
 */
async function cacheUserProfile(profile: UserProfileResponse): Promise<void> {
  try {
    await dbReady;
    await db.settings.put({
      key: CACHED_PROFILE_KEY,
      value: {
        uid: profile.uid,
        email: profile.email,
        role: profile.role,
        organizationId: profile.organizationId,
      },
    });
  } catch (err) {
    console.warn('Failed to cache user profile:', err);
  }
}

/**
 * Read cached user profile from Dexie
 */
async function getCachedUserProfile(): Promise<UserProfileResponse | null> {
  try {
    await dbReady;
    const setting = await db.settings.get(CACHED_PROFILE_KEY);
    if (!setting || typeof setting.value !== 'object' || setting.value === null) {
      return null;
    }
    const cached = setting.value as Record<string, unknown>;
    // Validate required fields
    if (typeof cached.uid !== 'string' || typeof cached.role !== 'string') {
      return null;
    }
    return {
      uid: cached.uid as string,
      email: (cached.email as string | null) ?? null,
      role: cached.role as string,
      organizationId: (cached.organizationId as string | null) ?? null,
    };
  } catch (err) {
    console.warn('Failed to read cached user profile:', err);
    return null;
  }
}

/**
 * Clear cached user profile (on sign out)
 */
async function clearCachedUserProfile(): Promise<void> {
  try {
    await dbReady;
    await db.settings.delete(CACHED_PROFILE_KEY);
  } catch (err) {
    console.warn('Failed to clear cached user profile:', err);
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    error: null,
  });

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Try online auth, then fall back to cached profile
        let token: string | null = null;
        try {
          token = await firebaseUser.getIdToken();
          setAuthToken(token);
        } catch {
          // Token refresh failed (offline + expired) — will use cache below
        }

        let profile: UserProfileResponse | null = null;
        if (token) {
          profile = await fetchUserProfile();
        }

        if (profile) {
          // Online path succeeded — cache for future offline use
          await cacheUserProfile(profile);
          setState({
            user: transformUser(firebaseUser, profile),
            isLoading: false,
            isAuthenticated: true,
            error: null,
          });
        } else {
          // Offline or network error — fall back to cached profile
          const cached = await getCachedUserProfile();
          if (cached && cached.uid === firebaseUser.uid) {
            if (token) setAuthToken(token);
            setState({
              user: transformUser(firebaseUser, cached),
              isLoading: false,
              isAuthenticated: true,
              error: null,
            });
          } else {
            setState({
              user: null,
              isLoading: false,
              isAuthenticated: false,
              error: 'Network required — please connect to the internet and try again',
            });
          }
        }
      } else {
        setAuthToken(null);
        setState({
          user: null,
          isLoading: false,
          isAuthenticated: false,
          error: null,
        });
      }
    });

    return () => unsubscribe();
  }, []);

  // Sign in with email and password
  const signIn = useCallback(async (credentials: LoginCredentials) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      // Auth state change listener will update the state
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sign in';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      throw error;
    }
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      setAuthToken(null);
      await clearCachedUserProfile();
      await settingsService.clearCache();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }, []);

  // Refresh the auth token
  const refreshToken = useCallback(async (): Promise<string | null> => {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;

    try {
      const token = await currentUser.getIdToken(true); // Force refresh
      setAuthToken(token);
      return token;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  }, []);

  // Send password reset email
  // Note: We catch errors silently to prevent email enumeration attacks
  const resetPassword = useCallback(async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch {
      // Silently succeed to prevent email enumeration
      // Do not log errors - prevents information leakage in browser console
    }
  }, []);

  // Register the refresh callback with api-client for 401 handling
  useEffect(() => {
    setTokenRefreshCallback(refreshToken);
    return () => setTokenRefreshCallback(null);
  }, [refreshToken]);

  const value: AuthContextValue = {
    ...state,
    signIn,
    signOut,
    refreshToken,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext, auth };
